import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { QuoteService } from 'src/app/admin-core/apiservices/quote.service';
import { AlertService } from 'src/app/admin-core/services';
import { Constants } from 'src/app/admin-core/constants';
import { firstValueFrom } from 'rxjs';
import {FormAutofocusDirective} from '../../directives/form-autofocus.directive';

@Component({
  hostDirectives: [FormAutofocusDirective],
  standalone: false,
    selector: 'app-update-storage-fee-delivery-modal',
    templateUrl: './update-storage-fee-delivery-modal.component.html',
    styleUrls: ['./update-storage-fee-delivery-modal.component.css', '../../../app.component.css']
})
export class UpdateStorageFeeDeliveryModalComponent implements OnInit, OnChanges {
    @Input() modelOrder!: any;

    deliveries: any[] = [];
    isLoading: boolean = false;
    isInitialized: boolean = false;

    constructor(
        public activeModel: NgbActiveModal,
        private quoteService: QuoteService,
        private alertService: AlertService
    ) {}

    ngOnInit(): void {
        // Initialize deliveries if modelOrder is already set
        if (this.modelOrder) {
            this.initializeDeliveries();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Initialize deliveries when modelOrder is set
        if (changes['modelOrder'] && changes['modelOrder'].currentValue) {
            this.initializeDeliveries();
        }
    }

    private initializeDeliveries(): void {
        this.isInitialized = false;
        this.deliveries = [];

        // Safeguard: If consumable order, initialize with empty deliveries and close modal
        if (this.modelOrder?.is_consumable_order === 1 || this.modelOrder?.is_consumable_order === true) {
            this.alertService.warning('Storage fees are not applicable for consumable orders.');
            this.activeModel.close(false);
            return;
        }

        // Deliveries that hold at least one line, from the order's lines. A delivery left holding no line
        // (e.g. after merging a split line back) is not billed storage and is not listed elsewhere, so it
        // is not offered here either. If the lines are not loaded, every delivery is listed as before.
        const deliveriesWithLines = new Set<number>();
        (this.modelOrder?.quote_product || []).forEach((line: any) => {
            const links = Array.isArray(line.quote_schedule_delivery_product)
                ? line.quote_schedule_delivery_product
                : [line.quote_schedule_delivery_product];
            links.forEach((link: any) => {
                if (link?.quote_schedule_delivery_id) deliveriesWithLines.add(Number(link.quote_schedule_delivery_id));
            });
        });

        this.modelOrder?.quote_address?.forEach((addr: any) => {
            addr.quote_schedule_delivery?.forEach((del: any) => {
                const isDelivered = del.status_id === Constants.STATUS_DELIVERD_ID ||
                    del.status_id === Constants.STATUS_DELIVERY_DESPATCHED_ID;
                if (!isDelivered && deliveriesWithLines.size > 0 && !deliveriesWithLines.has(Number(del.id))) {
                    return;
                }
                this.deliveries.push({
                    id: del.id,
                    delivery_code: del.delivery_code,
                    is_consider_for_storage_fees: isDelivered ? false : !!del.is_consider_for_storage_fees,
                    isDelivered
                });
            });
        });
        // Mark as initialized after data is ready
        this.isInitialized = true;
    }

    cancel() {
        this.activeModel.close(false);
    }

    async onUpdate() {
        this.isLoading = true;

        const selectedCount = this.deliveries.filter(d => d.is_consider_for_storage_fees).length;
        const totalCount = this.deliveries.length;
        const anySelected = selectedCount > 0;

        const payload = {
            is_storage_fee_applicable: anySelected,
            delivery_storage_fees: this.deliveries.map(d => ({
                delivery_id: d.id,
                is_consider_for_storage_fees: d.is_consider_for_storage_fees
            }))
        };

        try {
            await firstValueFrom(
                this.quoteService.updateStorageFeeApplicable(payload, this.modelOrder.id)
            );
            this.activeModel.close(true);

            // Generate descriptive success message
            let successMessage = '';
            if (selectedCount === 0) {
                successMessage = 'All deliveries removed from storage fee calculation';
            } else if (selectedCount === totalCount) {
                successMessage = 'All deliveries included in storage fee calculation';
            } else {
                successMessage = `${selectedCount} of ${totalCount} deliveries included in storage fee calculation`;
            }
            this.alertService.success(successMessage);
        } catch (err) {
            console.error('Failed to update storage fees', err);
            this.alertService.error('Failed to update storage fee deliveries.');
        } finally {
            this.isLoading = false;
        }
    }
}
